import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, ArrowRight, ArrowLeft, Download, Rocket } from "lucide-react";

const STEPS = ["Hôtel", "Chambres", "Trunk 3CX", "Touches", "Finalisation"];

function apiErr(d) {
  if (!d) return "Erreur";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e.msg || "").join(", ");
  return "Erreur";
}

function Field({ label, value, onChange, placeholder, hint, type = "text" }) {
  return (
    <div>
      <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">{label}</div>
      <Input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 rounded-sm text-sm"
      />
      {hint && <p className="mt-1 text-[11px] text-zinc-400">{hint}</p>}
    </div>
  );
}

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [servers, setServers] = useState([]);
  const [hotelId, setHotelId] = useState(null);
  const [pushed, setPushed] = useState(null);

  const [hotel, setHotel] = useState({ name: "", sip_domain: "", extension_prefix: "1", sip_server_id: "", voice_vlan: "" });
  const [rooms, setRooms] = useState({ start: "101", count: "10" });
  const [trunk, setTrunk] = useState({ trunk_host: "", trunk_port: "5060" });
  const [key9, setKey9] = useState({ key: "9", label: "Réception", target: "" });

  useEffect(() => {
    api.get("/servers").then((r) => setServers(r.data)).catch(() => {});
  }, []);

  const guard = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    }
    setBusy(false);
  };

  const createHotel = () =>
    guard(async () => {
      const { data } = await api.post("/hotels", {
        name: hotel.name,
        sip_domain: hotel.sip_domain,
        extension_prefix: hotel.extension_prefix || "1",
        sip_server_id: hotel.sip_server_id,
        voice_vlan: hotel.voice_vlan || "",
      });
      setHotelId(data.id);
      setStep(2);
    });

  const createRooms = () =>
    guard(async () => {
      const { data } = await api.post(`/hotels/${hotelId}/rooms/bulk`, {
        start: Number(rooms.start),
        count: Number(rooms.count),
      });
      toast.success(`${data.created} chambres créées`);
      setStep(3);
    });

  const saveTrunk = () =>
    guard(async () => {
      await api.put(`/hotels/${hotelId}/trunk`, {
        name: "3CX",
        trunk_host: trunk.trunk_host,
        trunk_port: Number(trunk.trunk_port),
        auth_id: (hotel.sip_domain.split(".")[0] || "hotel") + "_trunk",
        auth_password: Math.random().toString(36).slice(2, 16),
        register: false,
        transport: "udp",
        codecs: ["g722", "alaw", "ulaw"],
      });
      setStep(4);
    });

  const finalize = (skipKey) =>
    guard(async () => {
      if (!skipKey && key9.target) {
        await api.post(`/hotels/${hotelId}/speed-dials`, {
          key: key9.key,
          label: key9.label,
          target: key9.target,
          via: "trunk",
        });
      }
      const res = await api.post(`/hotels/${hotelId}/push`);
      setPushed(res.data.ok);
      setStep(5);
    });

  const download = async (path, fallback) => {
    try {
      const { data } = await api.get(path);
      const blob = new Blob([data.content], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename || fallback;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Téléchargement impossible");
    }
  };

  const canNext =
    (step === 1 && hotel.name && hotel.sip_domain && hotel.sip_server_id) ||
    (step === 2 && Number(rooms.count) > 0) ||
    (step === 3 && trunk.trunk_host) ||
    step === 4;

  return (
    <div data-testid="onboarding-page" className="max-w-3xl pb-12">
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Mise en service</p>
      <h1 className="mt-1 font-head text-3xl font-black tracking-tighter text-zinc-900">
        Onboarding d'un hôtel
      </h1>
      <p className="mt-1 text-sm text-zinc-500">
        Provisionnez un hôtel de bout en bout : compte, chambres, trunk 3CX, touches, puis poussez
        la configuration vers le serveur Asterisk.
      </p>

      {/* Stepper */}
      <div className="mt-6 flex items-center">
        {STEPS.map((s, i) => {
          const n = i + 1;
          const active = n === step;
          const ok = n < step;
          return (
            <div key={s} className="flex flex-1 items-center last:flex-none">
              <div className="flex items-center gap-2">
                <span
                  className={`flex h-7 w-7 items-center justify-center rounded-sm font-mono text-xs font-bold ${
                    ok ? "bg-emerald-600 text-white" : active ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-400"
                  }`}
                >
                  {ok ? <Check className="h-4 w-4" /> : n}
                </span>
                <span className={`text-xs font-semibold ${active ? "text-zinc-900" : "text-zinc-400"}`}>{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className="mx-3 h-px flex-1 bg-zinc-200" />}
            </div>
          );
        })}
      </div>

      <div className="mt-6 rounded-sm border border-zinc-200 bg-white p-6">
        {step === 1 && (
          <div className="space-y-4">
            <Field label="Nom de l'hôtel" value={hotel.name} onChange={(v) => setHotel({ ...hotel, name: v })} placeholder="Nom de l'hôtel" />
            <Field label="Domaine SIP" value={hotel.sip_domain} onChange={(v) => setHotel({ ...hotel, sip_domain: v })} placeholder="lutece.hotelsip.io" hint="Identifie l'hôtel dans la config Asterisk (contextes, fichiers)." />
            <Field label="Préfixe d'extension" value={hotel.extension_prefix} onChange={(v) => setHotel({ ...hotel, extension_prefix: v })} placeholder="1" hint="Préfixe 1 → chambres 101, 102… (distinct par hôtel pour éviter les collisions)." />
            <Field label="VLAN voix (optionnel)" value={hotel.voice_vlan} onChange={(v) => setHotel({ ...hotel, voice_vlan: v })} placeholder="ex. 200" hint="VLAN voix de l'hôtel pour le provisioning des téléphones (livré par le switch via CDP/LLDP)." />
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Serveur SIP</div>
              <select
                value={hotel.sip_server_id}
                onChange={(e) => setHotel({ ...hotel, sip_server_id: e.target.value })}
                className="mt-1 w-full rounded-sm border border-zinc-200 bg-white px-3 py-2 text-sm"
              >
                <option value="">— Choisir un serveur —</option>
                {servers.map((s) => (
                  <option key={s.id} value={s.id}>{s.name} ({s.ip})</option>
                ))}
              </select>
              {servers.length === 0 && (
                <p className="mt-1 text-[11px] text-amber-600">Aucun serveur. Créez-en un via Déploiement / Serveurs SIP d'abord.</p>
              )}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <p className="text-sm text-zinc-500">Créez les comptes SIP des chambres en lot.</p>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Première extension" value={rooms.start} onChange={(v) => setRooms({ ...rooms, start: v })} placeholder="101" />
              <Field label="Nombre de chambres" value={rooms.count} onChange={(v) => setRooms({ ...rooms, count: v })} placeholder="10" />
            </div>
            <p className="text-[11px] text-zinc-400">Les secrets SIP sont générés automatiquement (exportables en CSV à la fin).</p>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <p className="text-sm text-zinc-500">Trunk vers le 3CX de l'hôtel (mode IP-based, sans enregistrement).</p>
            <Field label="Hôte 3CX (IP)" value={trunk.trunk_host} onChange={(v) => setTrunk({ ...trunk, trunk_host: v })} placeholder="193.105.165.24" hint="IP publique du 3CX (recommandé pour un identify fiable)." />
            <Field label="Port SIP du 3CX" value={trunk.trunk_port} onChange={(v) => setTrunk({ ...trunk, trunk_port: v })} placeholder="5060" />
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <p className="text-sm text-zinc-500">Touche type 9 → extension 3CX (ex. la réception). Optionnel.</p>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Touche" value={key9.key} onChange={(v) => setKey9({ ...key9, key: v })} placeholder="9" />
              <Field label="Libellé (nom affiché)" value={key9.label} onChange={(v) => setKey9({ ...key9, label: v })} placeholder="Réception" />
              <Field label="Extension 3CX" value={key9.target} onChange={(v) => setKey9({ ...key9, target: v })} placeholder="1000" />
            </div>
            <p className="text-[11px] text-zinc-400">Le libellé s'affiche côté 3CX et sur la chambre lors des appels.</p>
          </div>
        )}

        {step === 5 && (
          <div className="space-y-5 text-center">
            <div className="flex flex-col items-center">
              <span className={`flex h-12 w-12 items-center justify-center rounded-sm ${pushed ? "bg-emerald-600" : "bg-amber-500"} text-white`}>
                {pushed ? <Check className="h-6 w-6" /> : <Rocket className="h-6 w-6" />}
              </span>
              <h2 className="mt-3 font-head text-xl font-bold text-zinc-900">
                {pushed ? "Hôtel provisionné et configuration poussée" : "Hôtel créé — push à vérifier"}
              </h2>
              <p className="mt-1 text-sm text-zinc-500">Récupérez les comptes SIP et la procédure 3CX.</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-3 text-left">
              <button onClick={() => download(`/hotels/${hotelId}/rooms/export`, "comptes-sip.csv")} className="rounded-sm border border-zinc-200 p-3 text-left hover:bg-zinc-50">
                <Download className="h-4 w-4 text-zinc-400" />
                <div className="mt-2 text-xs font-semibold text-zinc-900">Comptes SIP (CSV)</div>
                <div className="text-[11px] text-zinc-500">Provisioning des téléphones.</div>
              </button>
              <button onClick={() => download("/deploy/trunk3cx", "HotelSIP-Asterisk-Trunk-V20.xml")} className="rounded-sm border border-zinc-200 p-3 text-left hover:bg-zinc-50">
                <Download className="h-4 w-4 text-zinc-400" />
                <div className="mt-2 text-xs font-semibold text-zinc-900">Modèle trunk 3CX V20</div>
                <div className="text-[11px] text-zinc-500">À importer dans 3CX.</div>
              </button>
              <button onClick={() => download("/deploy/guide3cx", "3CX_V20_SETUP.md")} className="rounded-sm border border-zinc-200 p-3 text-left hover:bg-zinc-50">
                <Download className="h-4 w-4 text-zinc-400" />
                <div className="mt-2 text-xs font-semibold text-zinc-900">Procédure 3CX V20</div>
                <div className="text-[11px] text-zinc-500">Trunk + règles d'appel.</div>
              </button>
            </div>
            <Button onClick={() => navigate(`/hotels/${hotelId}`)} className="rounded-sm bg-blue-600 hover:bg-blue-700">
              Ouvrir la fiche de l'hôtel <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Nav */}
        {step < 5 && (
          <div className="mt-6 flex items-center justify-between border-t border-zinc-100 pt-4">
            <Button variant="ghost" disabled={step === 1 || busy} onClick={() => setStep(step - 1)} className="rounded-sm text-zinc-500">
              <ArrowLeft className="mr-1 h-4 w-4" /> Retour
            </Button>
            <div className="flex gap-2">
              {step === 4 && (
                <Button variant="outline" disabled={busy} onClick={() => finalize(true)} className="rounded-sm">
                  Sans touche
                </Button>
              )}
              <Button
                data-testid="onboarding-next"
                disabled={!canNext || busy}
                onClick={() => (step === 1 ? createHotel() : step === 2 ? createRooms() : step === 3 ? saveTrunk() : finalize(false))}
                className="rounded-sm bg-zinc-900 hover:bg-zinc-800"
              >
                {step === 4 ? "Pousser la config" : "Continuer"} <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
